import { useEffect, useRef } from "react";
import type {
  EventWithDataHandler,
  FormErrors,
  TypedWrappedFieldProps,
  WrappedFieldProps,
} from "redux-form";
import { Field, Fields, submit } from "redux-form";

import {
  Avatar,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  AttachmentFileIcon,
  AttachmentImageIcon,
  SendMessageIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { SchedulePromptModal } from "@js/apps/messenger/components/schedule-prompt-modal";
import { DEFAULT_MESSENGER_TEXTBOX_PLACEHOLDER } from "@js/apps/messenger/components/textbox/constants";
import type { TextBoxFormValues } from "@js/apps/messenger/hooks";
import { useTextBox } from "@js/apps/messenger/hooks/use-textbox";
import { getFormId } from "@js/apps/messenger/utils/get-messenger-form-id";
import { createFormInstance } from "@js/forms/components";
import { TextField } from "@js/forms/fields";
import { useAppDispatch } from "@js/hooks";
import type { User } from "@js/types/auth";
import type { MessageRoom } from "@js/types/messenger";
import { validateAttachments } from "@js/utils/redux-form";

import { useMessagesContext } from "../../context";
import { MessageFilesUploadField } from "../message-files-upload-field";
import { SchedulePrompt } from "../schedule-prompt/schedule-prompt";

import { TextboxAttachmentsPreview } from "./textbox-attachments-preview";
import { TextboxLinkPreviewField } from "./textbox-link-preview";

import styles from "./styles.module.scss";

const validate = (values: TextBoxFormValues) => {
  const errors: FormErrors<TextBoxFormValues, string> = {};

  const attachmentsError = validateAttachments(values);
  if (attachmentsError) {
    errors["attachments"] = attachmentsError;
  }

  return errors;
};

const MESSENGER_TEXTBOX_FORM_ID = "messenger-textbox-form";
const MessengerTextboxForm = createFormInstance<TextBoxFormValues, unknown>(
  MESSENGER_TEXTBOX_FORM_ID,
  { validate },
);

export type TextBoxProps = {
  initialMessage?: string;
  room?: MessageRoom;
  autoFocus?: boolean;
  placeholder?: string;
};

export const TextBox = ({
  autoFocus = true,
  initialMessage = "",
  placeholder = DEFAULT_MESSENGER_TEXTBOX_PLACEHOLDER,
  room,
}: TextBoxProps) => {
  const {
    user,
    canNotSendMessageReason,
    canSendMessage,
    initialValues,
    onSubmit,
    formId,
    messageError,
  } = useTextBox({ room, initialMessage });
  const dispatch = useAppDispatch();

  const messagesStore = useMessagesContext();

  if (!room) {
    return null;
  }

  const sendOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;

    if (!e.shiftKey && e.key === "Enter" && target.value) {
      e.preventDefault();
      if (room.id) {
        dispatch(submit(formId));
      }
    }
  };

  const roomId = room.id;

  const handleMessageChange: EventWithDataHandler<
    React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  > = (event) => {
    if (!event) return;

    const newMessage = event.target.value;
    messagesStore.set(roomId, newMessage);
  };

  return (
    <>
      {!!messageError && (
        <Typography
          component="p"
          variant="paragraph"
          color="negative"
          size="small"
          sx={{ marginBottom: 0.5 }}
        >
          {messageError}
        </Typography>
      )}
      <MessengerTextboxForm
        className={styles.textbox}
        initialValues={initialValues}
        form={formId}
        key={formId}
        onSubmit={onSubmit}
      >
        <div className={styles.fields}>
          <Stack direction="row">
            {user && (
              <Avatar size="full" src={user} className={styles.avatar} />
            )}
            <Field
              id="messenger-text-box"
              name="message"
              normalize={(message) => {
                if (message.length === 0) return null;
                return message;
              }}
              component={TextField}
              onChange={handleMessageChange}
              autoFocus={autoFocus}
              disabled={!canSendMessage}
              multiline
              onKeyDown={sendOnEnter}
              minRows={1}
              placeholder={placeholder}
              className={styles.textboxInput}
              slotProps={{ htmlInput: { maxLength: 5000 } }}
              sx={{ alignSelf: "center" }}
            />
          </Stack>
          <Fields
            names={["attachments", "showAttachmentField"]}
            component={MessageFilesUploadField}
          />
          <Field
            name="calendar_link"
            component={TextboxLinkPreviewField}
            activeRoom={room}
          />
          <Field name={"attachments"} component={TextboxAttachmentsPreview} />
        </div>
        <Fields
          names={["showAttachmentField", "attachments"]}
          component={MessengerTextboxActions}
          user={user}
          canNotSendMessageReason={canNotSendMessageReason}
          canSendMessage={canSendMessage}
          room={room}
        />
      </MessengerTextboxForm>
      <SchedulePromptModal activeRoom={room} />
    </>
  );
};

type MessengerTextboxActionsProps = {
  user: User;
  canNotSendMessageReason: string;
  canSendMessage: boolean;
  showAttachmentField: WrappedFieldProps;
  room: MessageRoom;
  attachments: TypedWrappedFieldProps<unknown[] | null>;
};

const MessengerTextboxActions = ({
  user,
  canNotSendMessageReason,
  canSendMessage,
  showAttachmentField,
  room,
  attachments,
}: MessengerTextboxActionsProps) => {
  const dispatch = useAppDispatch();
  const { setIsImageAttachment } = useMessagesContext();
  const actionsRef = useRef<HTMLDivElement>(null);
  const isShowAttachmentField = showAttachmentField.input.value;

  const handleOpenAttachmentField = () => {
    showAttachmentField.input.onChange(!isShowAttachmentField);
    setIsImageAttachment(false);
  };

  const handleOpenImageAttachmentField = () => {
    showAttachmentField.input.onChange(!isShowAttachmentField);
    setIsImageAttachment(!isShowAttachmentField);
  };

  useEffect(() => {
    if (isShowAttachmentField) {
      actionsRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  }, [isShowAttachmentField, attachments.input.value]);

  const formId = getFormId(room.id);

  return (
    <div ref={actionsRef}>
      <div className={styles.actionsContainer}>
        <div className={styles.secondaryActions}>
          <Tooltip title="Add an image" placement="top">
            <IconButton
              className={styles.icon}
              aria-label="Add image"
              variant="transparent"
              size="medium"
              onClick={handleOpenImageAttachmentField}
              sx={{
                "&:hover": { backgroundColor: "var(--yellow)" },
              }}
            >
              <AttachmentImageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add a file" placement="top">
            <IconButton
              className={styles.icon}
              variant="transparent"
              size="medium"
              onClick={handleOpenAttachmentField}
              aria-label="Add file"
              sx={{
                "&:hover": { backgroundColor: "var(--yellow)" },
              }}
            >
              <AttachmentFileIcon />
            </IconButton>
          </Tooltip>
          <SchedulePrompt
            user={user}
            activeRoom={room}
            className={styles.icon}
          />
        </div>
        <div className={styles.actions}>
          <Tooltip
            title={canNotSendMessageReason}
            disabled={!canNotSendMessageReason}
          >
            <span className={styles.submitButton}>
              <Button
                variant="primary"
                disabled={!canSendMessage}
                type="submit"
                //https://stackoverflow.com/a/56970849/15536964 -- (mobile) prevent hiding keyboard on Send button click
                onTouchEnd={(e) => {
                  e.preventDefault();
                  dispatch(submit(formId));
                }}
                endIcon={<SendMessageIcon />}
                shape="squared"
                fontWeight={400}
                size="x-small"
              >
                Send
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
